import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLooseProductContextSelector } from 'src/components/restructure/product/contexts/product-context'
import {
  makeProductDetailEvent,
  sendEvent,
} from 'src/utils/restructure/analytics'
import { publish } from 'src/components/restructure/product/productEvents'
import type { Variation } from 'src/components/product-page/Selector/types'

import type { SelectedVariation, SkuSpecificationsName } from './types'
import type { Product, SkuSpecificationsValue } from '../../../types/product'
import type { ProductItem } from '../../../types/productItem'
import { RenderedSelectors } from './RenderedSelectors'
import './styles.scss'

type Props = {
  isFromStickyBar?: boolean
  isFirstFoldRevampEnabled?: boolean
  classContainer?: string
  hasDisabledButtons?: boolean
  productFromProps?: Product
  currentSkuFromProps?: ProductItem
  setCurrentSkuFromProps?: (sku: ProductItem) => void
  initialSkuFromProps?: ProductItem
  variationsFromProps?: Variation[]
  setVariationsFromProps?: (variations: Variation[]) => void
}

export function SkuSelector({
  isFromStickyBar = false,
  isFirstFoldRevampEnabled = false,
  classContainer,
  hasDisabledButtons = false,
  productFromProps,
  setCurrentSkuFromProps,
  initialSkuFromProps,
  variationsFromProps,
  setVariationsFromProps,
  currentSkuFromProps,
}: Readonly<Props>) {
  const contextProduct = useLooseProductContextSelector((ctx) => ctx.product)
  const contextSetCurrentSku = useLooseProductContextSelector(
    (ctx) => ctx.setCurrentSku
  )

  const contextInitialSku = useLooseProductContextSelector(
    (ctx) => ctx.initialSku
  )

  const contextVariations = useLooseProductContextSelector(
    (ctx) => ctx.variations
  )

  const contextSetVariations = useLooseProductContextSelector(
    (ctx) => ctx.setVariations
  )

  const contextCurrentSku = useLooseProductContextSelector(
    (ctx) => ctx.currentSku
  )

  const product = productFromProps ?? contextProduct
  const setCurrentSku = setCurrentSkuFromProps ?? contextSetCurrentSku

  const initialSku = initialSkuFromProps ?? contextInitialSku
  const variations = variationsFromProps ?? contextVariations
  const setVariations = setVariationsFromProps ?? contextSetVariations
  const currentSku = currentSkuFromProps ?? contextCurrentSku

  const [localVariations, setLocalVariations] = useState(
    variationsFromProps ?? contextVariations
  )

  type SkuSpecificationValueWithManufacturerCode = SkuSpecificationsValue & {
    manufacturerCode?: string
  }

  const buildColorValues = (
    specification: Product['skuSpecifications'][number]
  ): SkuSpecificationValueWithManufacturerCode[] => {
    // Products relançados anualmente podem compartilhar o mesmo valor de Cor
    // (ex: "azul") entre manufacturerCodes diferentes mesclados na mesma PDP.
    // Como esse valor de especificação é global no catálogo, expandimos as
    // opções por manufacturerCode em vez de usar apenas o nome como chave.
    const seenKeys = new Set<string>()
    const values: SkuSpecificationValueWithManufacturerCode[] = []

    specification.values.forEach((baseValue) => {
      product.items?.forEach((sku) => {
        if (sku.Cor?.[0] !== baseValue.name) {
          return
        }

        const key = `${baseValue.name}::${sku.manufacturerCode}`

        if (seenKeys.has(key)) {
          return
        }

        seenKeys.add(key)
        values.push({
          ...baseValue,
          id: key,
          manufacturerCode: sku.manufacturerCode,
        })
      })
    })

    return values
  }

  const { skuSpecifications } = useMemo(() => {
    if (product.productId === '11113') {
      const skuSpecificationsGiftCard = product.skuSpecifications
        .filter((skuSpecification) => skuSpecification.field.name === 'Valor')
        .map((specification) => {
          const valuesName = product?.items?.map(
            (sku) => sku[specification.field.name]?.[0]
          )

          return {
            ...specification,
            values: specification.values.filter((value) =>
              valuesName.includes(value.name)
            ),
          }
        })

      return { ...product, skuSpecifications: skuSpecificationsGiftCard }
    }

    return {
      ...product,
      skuSpecifications: product.skuSpecifications.map((specification) => {
        if (specification.field.name === 'Cor') {
          return { ...specification, values: buildColorValues(specification) }
        }

        const valuesName = product?.items?.map(
          (sku) => sku[specification.field.name]?.[0]
        )

        return {
          ...specification,
          values: specification.values.filter((value) =>
            valuesName.includes(value.name)
          ),
        }
      }),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]) as {
    skuSpecifications: Array<{
      field: Product['skuSpecifications'][number]['field']
      values: SkuSpecificationValueWithManufacturerCode[]
    }>
  }

  const checkSkuIsAvailable = useCallback(
    (selectedVariation: SelectedVariation[]) => {
      return product.items.some(
        (productItem) =>
          selectedVariation.every(
            (variation) =>
              productItem[variation.field]?.[0] === variation.value &&
              (!variation.manufacturerCode ||
                productItem.manufacturerCode === variation.manufacturerCode)
          ) && productItem.isAvailable
      )
    },
    [product.items]
  )

  const allPossibleVariations = useMemo(
    () =>
      skuSpecifications.map((variation) => {
        return {
          ...variation,
          selected: true,
          values: variation.values.map((variationValues) => ({
            ...variationValues,
            selected:
              variationValues.name === initialSku[variation.field.name]?.[0] &&
              (!variationValues.manufacturerCode ||
                variationValues.manufacturerCode ===
                  initialSku.manufacturerCode),
            isAvailable: checkSkuIsAvailable([
              {
                field: variation.field.name,
                value: variationValues.name,
                manufacturerCode: variationValues.manufacturerCode,
              },
            ]),
          })),
        }
      }),
    [checkSkuIsAvailable, initialSku, skuSpecifications]
  )

  const initialVariations = useMemo(() => {
    // Remove values from other variations that are not available for initial selected variation
    return allPossibleVariations.map((variation, index: number) => {
      if (index === 0) {
        return variation
      }

      const firstFieldName = allPossibleVariations[0].field.name
      const firstSelectedValue = initialSku[firstFieldName]?.[0]

      return {
        ...variation,
        values: variation.values.map((variationValue) => ({
          ...variationValue,
          selected:
            variationValue.name === initialSku[variation.field.name]?.[0],
          isAvailable: checkSkuIsAvailable(
            [
              {
                field: firstFieldName,
                value: firstSelectedValue,
                manufacturerCode: initialSku.manufacturerCode,
              },
              { field: variation.field.name, value: variationValue.name },
            ].filter(Boolean) as SelectedVariation[]
          ),
        })),
      }
    })
  }, [allPossibleVariations, initialSku, checkSkuIsAvailable])

  const updateVariationsBySelectedValues = useCallback(
    (
      tempVariations: Variation[],
      selectedVariations: SelectedVariation[],
      nextVariation?: Variation
    ) => {
      const nextOriginalVariation = allPossibleVariations.find(
        (variationItem) =>
          variationItem.field.name === nextVariation?.field.name
      )

      const itemsBySelectedVariations = product.items.filter((item) =>
        selectedVariations.every(
          (selectedVariation) =>
            item[selectedVariation.field]?.[0] === selectedVariation.value &&
            (!selectedVariation.manufacturerCode ||
              item.manufacturerCode === selectedVariation.manufacturerCode)
        )
      )

      const possibleValuesBySelectedVariations =
        nextOriginalVariation?.values.filter((variationValue) =>
          itemsBySelectedVariations.find(
            (productItem) =>
              productItem[nextOriginalVariation.field.name]?.[0] ===
                variationValue.name &&
              (!variationValue.manufacturerCode ||
                productItem.manufacturerCode ===
                  variationValue.manufacturerCode)
          )
        )

      const newNextVariation = {
        ...nextOriginalVariation,
        values: possibleValuesBySelectedVariations,
      }

      return tempVariations.map((variation) => {
        if (variation.field.name !== nextVariation?.field.name) {
          return { ...variation }
        }

        if (newNextVariation?.values?.length) {
          const mappedVariations = newNextVariation.values.map(
            (variationValues) => ({
              ...variationValues,
              isAvailable: checkSkuIsAvailable([
                ...selectedVariations,
                {
                  field: variation.field.name,
                  value: variationValues.name,
                  manufacturerCode: variationValues.manufacturerCode,
                },
              ]),
            })
          )

          const availables = mappedVariations.filter(
            (mappedVariation) => mappedVariation.isAvailable
          )

          return {
            ...variation,
            selected: availables.length === 1,
            values: mappedVariations.map((mappedVariation) => ({
              ...mappedVariation,
              selected:
                mappedVariation.isAvailable &&
                availables.length === 1 &&
                mappedVariation.name === availables[0].name &&
                mappedVariation.manufacturerCode ===
                  availables[0].manufacturerCode,
            })),
          }
        }

        return { ...variation }
      })
    },
    [checkSkuIsAvailable, allPossibleVariations, product.items]
  )

  const getSelectedValues = useCallback((specifications: Variation[]) => {
    return specifications
      .filter((variation) => variation.selected)
      .map((variation) => {
        const selectedValue = variation.values.find((value) => value.selected)

        return {
          field: variation.field.name,
          value: selectedValue?.name as string,
          manufacturerCode: selectedValue?.manufacturerCode,
        }
      })
  }, [])

  useEffect(() => {
    if (!initialVariations) {
      return
    }

    const nextVariant =
      initialVariations.find((variation) => !variation.selected) ??
      initialVariations[1]

    const selectedValues = getSelectedValues(initialVariations).filter(
      (selectedVariation) => selectedVariation.field !== nextVariant?.field.name
    )

    const validatedVariations = updateVariationsBySelectedValues(
      initialVariations,
      selectedValues,
      nextVariant
    )

    setVariations(validatedVariations)
    setLocalVariations(validatedVariations)
  }, [
    getSelectedValues,
    initialVariations,
    updateVariationsBySelectedValues,
    setVariations,
    setLocalVariations,
  ])

  const getProductByCurrentVariation = (
    productItemName: SkuSpecificationsName,
    value: string,
    manufacturerCode?: string
  ) => {
    const matchesManufacturerCode = (productItem: ProductItem) =>
      !manufacturerCode || productItem.manufacturerCode === manufacturerCode

    const productByCurrentVariation =
      product.items.find(
        (productItem) =>
          productItem[productItemName]?.[0] === value &&
          matchesManufacturerCode(productItem) &&
          productItem.isAvailable
      ) ??
      (product.items.find(
        (productItem) =>
          productItem[productItemName]?.[0] === value &&
          matchesManufacturerCode(productItem) &&
          !productItem.isAvailable
      ) as ProductItem)

    setCurrentSku(productByCurrentVariation)

    // this updates URL without reloading the page
    window.history.pushState({}, '', `/${productByCurrentVariation.linkText}/p`)
  }

  const onClickVariation = (
    name: SkuSpecificationsName,
    value: string,
    manufacturerCode?: string
  ) => {
    publish('selectVariation')
    publish('hideSelectorNotification')

    const idxCurrentVariation = localVariations.findIndex(
      (variation) => variation.field.name === name
    )

    const nextVariant = allPossibleVariations[Number(idxCurrentVariation) + 1]

    const updatedVariations = localVariations.map((vari, index) => {
      if (idxCurrentVariation === 0) {
        getProductByCurrentVariation(name, value, manufacturerCode)
      }

      if (idxCurrentVariation === index) {
        return {
          ...vari,
          selected: true,
          values: vari.values.map((item) => ({
            ...item,
            selected:
              item.name === value &&
              (!manufacturerCode || item.manufacturerCode === manufacturerCode),
          })),
        }
      }

      if (idxCurrentVariation < index) {
        return {
          ...vari,
          selected: false,
          values: vari.values.map((item) => ({
            ...item,
            selected: false,
          })),
        }
      }

      if (idxCurrentVariation > index) {
        return { ...vari }
      }

      return { ...vari }
    })

    const selectedValues = getSelectedValues(updatedVariations)

    const validatedVariations = updateVariationsBySelectedValues(
      updatedVariations,
      selectedValues,
      nextVariant
    )

    if (!nextVariant || nextVariant.selected) {
      try {
        const selectedProduct =
          findProductItemBySelectedVariations(validatedVariations)

        if (selectedProduct) {
          sendEventToChangeSelectedProduct(selectedProduct)
        }
      } catch {
        console.info('')
      }
    }

    setVariations(validatedVariations)
    setLocalVariations(validatedVariations)
  }

  const sendEventToChangeSelectedProduct = useCallback(
    (selectedProductSku: ProductItem) => {
      const event = makeProductDetailEvent(
        product,
        selectedProductSku,
        'skuSelector'
      )

      sendEvent(event)
    },
    [product]
  )

  const findProductItemBySelectedVariations = useCallback(
    (selectedVariations: Variation[]) => {
      const matchesVariation = (item: ProductItem, variation: Variation) => {
        const selectedValue = variation.values.find((value) => value.selected)

        return (
          item[variation.field.name]?.[0] === selectedValue?.name &&
          (!selectedValue?.manufacturerCode ||
            item.manufacturerCode === selectedValue.manufacturerCode)
        )
      }

      return (
        product.items.find(
          (item) =>
            selectedVariations.every((variation) =>
              matchesVariation(item, variation)
            ) && item.isAvailable
        ) ??
        product.items.find((item) =>
          selectedVariations.every((variation) =>
            matchesVariation(item, variation)
          )
        )
      )
    },
    [product.items]
  )

  useEffect(() => {
    const productBySelectedVariations =
      findProductItemBySelectedVariations(localVariations)

    if (productBySelectedVariations) {
      setCurrentSku(productBySelectedVariations)
    }
  }, [
    setCurrentSku,
    product,
    variations,
    localVariations,
    findProductItemBySelectedVariations,
  ])

  useEffect(() => {
    if (
      contextVariations &&
      JSON.stringify(contextVariations) !== JSON.stringify(localVariations)
    ) {
      setLocalVariations(contextVariations)
    }
  }, [contextVariations, localVariations])

  /**
   * Selects the first unselected variation if there is only one unselected variation
   * Ex: user selects a different color variantion, then this function will select the first unselected size variation
   */
  useEffect(() => {
    function updateInitialUnselectedVariation() {
      if (!currentSku || !localVariations.length) {
        return
      }

      const unselectedVariation = localVariations.find(
        (variation) => variation.selected === false
      )

      if (!unselectedVariation) {
        return
      }

      const selectedValues = getSelectedValues(localVariations)

      if (selectedValues.length !== 1 || localVariations.length <= 1) {
        return
      }

      const updatedVariations = localVariations.map((variation) => {
        if (variation.field.name === unselectedVariation.field.name) {
          return {
            ...variation,
            selected: true,
            values: variation.values.map((item) => ({
              ...item,
              selected:
                item.name === currentSku[variation.field.name]?.[0] &&
                (!item.manufacturerCode ||
                  item.manufacturerCode === currentSku.manufacturerCode),
            })),
          }
        }

        return { ...variation }
      })

      const selectedProduct =
        findProductItemBySelectedVariations(updatedVariations)

      if (!selectedProduct) {
        return
      }

      setCurrentSku(selectedProduct)
      setVariations(updatedVariations)
      setLocalVariations(updatedVariations)
    }

    updateInitialUnselectedVariation()
  }, [
    currentSku,
    findProductItemBySelectedVariations,
    getSelectedValues,
    setCurrentSku,
    setVariations,
    localVariations,
    setLocalVariations,
    variations,
  ])

  if (!localVariations.length) {
    return <></>
  }

  return (
    <div className={`flex flex-col gap-7 pt-4 ${classContainer ?? ''}`}>
      <RenderedSelectors
        variations={localVariations}
        onClickVariation={onClickVariation}
        isFromStickyBar={isFromStickyBar}
        isFirstFoldRevampEnabled={isFirstFoldRevampEnabled}
        hasDisabledButtons={hasDisabledButtons}
        productFromProps={product}
        currentSkuFromProps={currentSku}
      />
    </div>
  )
}
